/**
 * WHERE a citation opens — one definition, shared by search and the Assistant.
 *
 * A search hit and an Assistant citation point at the same two things: a
 * transaction record, or a controlled document. Both must land on the screen a
 * reader could have bookmarked — the record editor at `?mode=edit&rec=<txn_id>`,
 * a document at `<repository>?doc=<id>` — and they must land on the SAME one,
 * or "open the record this answer came from" quietly means something different
 * depending on which panel the reader used.
 *
 * So the URL contract lives here rather than in each host's open handler. The
 * hosts still own navigation (the platform Run view pushes a router URL, the
 * standalone runtime another): they receive an `OpenTarget` and decide how to
 * get there, which is the same split `GlobalSearchClient` makes for the query.
 *
 * A citation surface that has no handler falls back to an ordinary link — see
 * `AssistantChat`. That is why `objName`/`params` are plain strings: they have
 * to survive being turned back into an href.
 */

import type { GlobalSearchHit } from './contracts'

/** An object of this app, plus the query parameters that select a screen state. */
export interface OpenTarget {
  objName: string
  params: Record<string, string>
}

/** A host's navigation. Optional wherever a link fallback exists. */
export type OpenTargetHandler = (target: OpenTarget) => void

/** The record editor, keyed by the executor's `txn_id`. */
export function recordTarget(objName: string, recordKey: unknown): OpenTarget | null {
  const obj = String(objName || '').trim()
  const rec = recordKey == null ? '' : String(recordKey).trim()
  if (!obj || !rec) return null
  return { objName: obj, params: { mode: 'edit', rec } }
}

/** One document, in the repository it lives in. */
export function documentTarget(repoObj: string, documentId: string): OpenTarget | null {
  const obj = String(repoObj || '').trim()
  const doc = String(documentId || '').trim()
  if (!obj || !doc) return null
  return { objName: obj, params: { doc } }
}

/**
 * A NEW record on `objName`, pre-filled from a task's seed.
 *
 * The seed itself never travels in the URL. It can carry a whole header plus
 * detail rows, it is often larger than a URL should be, and it is data the
 * server must re-authorise on every open — so the link carries the task key
 * and the screen fetches the vetted payload behind it. That also keeps the
 * link short enough to share, and makes an expired or reassigned task fail
 * cleanly at the fetch instead of opening a form full of someone else's data.
 *
 * `formNo` selects the form within a multi-form transaction; omit it for the
 * header form.
 */
/**
 * The Support module's synthetic menu entry in the Run view, and the issue to
 * open on it. A work-list task ("approve the plan for #42") and a
 * notification about an issue land on the same URL as a shared link to it.
 */
export const SUPPORT_NAV_ID = '__support'

export function supportIssueTarget(issueNumber: unknown, tab?: string): OpenTarget | null {
  const n = Number(issueNumber)
  const params: Record<string, string> = {}
  if (Number.isFinite(n) && n > 0) params.issue = String(Math.floor(n))
  if (tab) params.tab = tab
  return { objName: SUPPORT_NAV_ID, params }
}

export function createTarget(
  objName: string,
  opts: { formNo?: number; seedRef?: string } = {},
): OpenTarget | null {
  const obj = String(objName || '').trim()
  if (!obj) return null
  const params: Record<string, string> = { mode: 'add' }
  if (opts.formNo != null) params.form = String(opts.formNo)
  if (opts.seedRef) params.seed = String(opts.seedRef)
  return { objName: obj, params }
}

/** A screen with no record selected (a navigate intent). */
export function screenTarget(objName: string): OpenTarget | null {
  const obj = String(objName || '').trim()
  return obj ? { objName: obj, params: {} } : null
}

/**
 * Where one search hit opens, or null when it opens nowhere — a master indexed
 * as a table source has no editor, and the panel already refuses to navigate
 * for it. Returning null here keeps that decision in one place.
 */
export function hitTarget(hit: GlobalSearchHit): OpenTarget | null {
  if (!hit || hit.openable === false) return null
  return hit.kind === 'document'
    ? documentTarget(hit.repo_obj || hit.obj_name, hit.document_id)
    : recordTarget(hit.obj_name, hit.record_key)
}
